import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { environment } from '../../src/environments/environment';

export interface UserInfo {
  id: number;
  username: string;
  email: string;
  role: string;
  company_id?: number;
  token: string;
}

// Interface for superuser check response
export interface SuperuserCheckResponse {
  isSuperuser: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = environment.apiUrl;
  
  // BehaviorSubject to track authentication state
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private userInfoSubject = new BehaviorSubject<UserInfo | null>(null);
  
  // Expose as observables
  isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  userInfo$ = this.userInfoSubject.asObservable();
  
  constructor(private http: HttpClient) {
    this.checkExistingToken();
    this.checkUrlParams();
  }
  
  /**
   * Check for existing token in localStorage
   */
  private checkExistingToken(): void {
    const token = localStorage.getItem('auth_token');
    if (token) {
      
      // Clean the token to ensure it's properly formatted
      const cleanToken = token.trim();
      
      // If token was changed, update it in localStorage
      if (cleanToken !== token) {
        localStorage.setItem('auth_token', cleanToken);
      }
      
      // Validate token format
      const parts = cleanToken.split('.');
      if (parts.length !== 3) {
        console.error('[Auth Service] Token is not in valid JWT format!');
        // Clear invalid token
        this.clearAuthData();
        return;
      }
      
      // Check token expiration
      try {
        const payload = JSON.parse(atob(parts[1]));
        
        if (payload.exp) {
          const expiryDate = new Date(payload.exp * 1000);
          const now = new Date();
          
          if (expiryDate < now) {
            console.warn('[Auth Service] Token has expired, clearing authentication data');
            this.clearAuthData();
            return;
          }
          
        }
      } catch (e) {
        console.error('[Auth Service] Error parsing token payload:', e);
        this.clearAuthData();
        return;
      }
      
      // Extract user info from localStorage
      const username = localStorage.getItem('username');
      const role = localStorage.getItem('userRole');
      const userId = localStorage.getItem('userId');
      const companyId = localStorage.getItem('companyId');
      const email = localStorage.getItem('email');
      
      // Create user info object
      const userInfo: UserInfo = {
        id: userId ? parseInt(userId, 10) : 0,
        username: username || 'Unknown',
        email: email || '',
        role: role || 'Unknown',
        company_id: companyId ? parseInt(companyId, 10) : undefined,
        token: cleanToken
      };
      
      this.userInfoSubject.next(userInfo);
      this.isAuthenticatedSubject.next(true);
      
      
      // Debug token format
      this.debugToken(cleanToken);
    } else {
      console.log('[Auth Service] No authentication token found in localStorage');
    }
  }
  
  /**
   * Debug token format and validity
   */
  private debugToken(token: string): void {
    
    // Check for common issues
    if (token.startsWith(' ') || token.endsWith(' ')) {
      console.warn('[Auth Service] Token has leading/trailing whitespace!');
    }
    
    // Verify structure
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('[Auth Service] Token is not in valid JWT format (should have 3 parts)!');
      return;
    }
    
    try {
      // Test HTTP headers with this token
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      });
      
      headers.keys().forEach(key => {
        console.log(`[Auth Service] ${key}: ${key.toLowerCase() === 'authorization' ? 
          headers.get(key)?.substring(0, 15) + '...' : 
          headers.get(key)}`);
      });
      
      // We'll call the debug endpoint in a separate method to avoid initialization issues
      this.verifyTokenWithDebugEndpoint(token);
    } catch (e) {
      console.error('[Auth Service] Error during token debugging:', e);
    }
  }
  
  /**
   * Verify token with debug endpoint
   */
  private verifyTokenWithDebugEndpoint(token: string): void {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
    
    this.http.get(`${this.apiUrl}/auth/token-debug`, { headers }).subscribe({
      next: (response: any) => {
        console.log('[Auth Service] Token debug endpoint response:', response);
      },
      error: (error) => {
        console.error('[Auth Service] Error from token debug endpoint:', error);
      }
    });
  }
  
  /**
   * Check URL for auth parameters
   */
  private checkUrlParams(): void {
    if (typeof window !== 'undefined') {
      
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      if (token) {
        
        // Store token (ensure it's properly trimmed)
        const cleanToken = token.trim();
        localStorage.setItem('auth_token', cleanToken);
        
        // Extract and store other parameters
        const username = urlParams.get('username');
        const role = urlParams.get('role');
        const userId = urlParams.get('userId');
        const companyId = urlParams.get('companyId');
        const email = urlParams.get('email');
        
        if (username) localStorage.setItem('username', username);
        if (role) localStorage.setItem('userRole', role);
        if (userId) localStorage.setItem('userId', userId);
        if (companyId) localStorage.setItem('companyId', companyId);
        if (email) localStorage.setItem('email', email);
        
        // Create user info object
        const userInfo: UserInfo = {
          id: userId ? parseInt(userId, 10) : 0,
          username: username || 'Unknown',
          email: email || '',
          role: role || 'Unknown',
          company_id: companyId ? parseInt(companyId, 10) : undefined,
          token: cleanToken
        };
        
        this.userInfoSubject.next(userInfo);
        this.isAuthenticatedSubject.next(true);
        
        
        // Remove the parameters from URL
        const urlWithoutParams = window.location.pathname;
        window.history.replaceState({}, document.title, urlWithoutParams);
                
        // Debug token format
        this.debugToken(cleanToken);
      }
    }
  }
  
  /**
   * Get authentication headers for API requests
   */
  getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('auth_token');
    
    
    if (!token) {
      console.warn('[Auth Service] No auth token available for headers');
      return new HttpHeaders({
        'Content-Type': 'application/json'
      });
    }
    
    const cleanToken = token.trim();
    
    // Debug log the token beginning for verification
    
    // Create headers with proper formatting - ensure no extra spaces, ensure proper case
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${cleanToken}`
    });
  }
  
  /**
   * Login with username and password
   */
  login(username: string, password: string): Observable<any> {
    
    return this.http.post<any>(`${this.apiUrl}/auth/login`, { username, password }).pipe(
      tap(response => {
        if (response && response.token) {
          
          // Store auth data (ensure token is properly trimmed)
          const cleanToken = response.token.trim();
          localStorage.setItem('auth_token', cleanToken);
          
          if (response.user) {
            localStorage.setItem('username', response.user.username);
            localStorage.setItem('userRole', response.user.role);
            localStorage.setItem('userId', response.user.id.toString());
            localStorage.setItem('email', response.user.email || '');
            
            if (response.user.company_id) {
              localStorage.setItem('companyId', response.user.company_id.toString());
            }
            
            // Update behavior subjects
            const userInfo: UserInfo = {
              id: response.user.id,
              username: response.user.username,
              email: response.user.email || '',
              role: response.user.role,
              company_id: response.user.company_id,
              token: cleanToken
            };
            
            this.userInfoSubject.next(userInfo);
            this.isAuthenticatedSubject.next(true);
            
            console.log('[Auth Service] User info stored and subjects updated');
            
            // Debug token format
            this.debugToken(cleanToken);
          }
          
          console.log('[Auth Service] Login process completed successfully');
        } else {
          console.error('[Auth Service] Login response did not contain token');
        }
      }),
      catchError(error => {
        console.error('[Auth Service] Login failed:', error);
        return of({ success: false, error: error.message || 'Unknown error' });
      })
    );
  }
  
  /**
   * Logout user
   */
  logout(): void {
    console.log('[Auth Service] Logging out user');
    
    // Get the current token
    const token = this.getToken();
    
    if (token) {
      // Call the logout endpoint if we have a token
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      });
      
      // Call the backend logout endpoint
      this.http.post(`${this.apiUrl}/auth/logout`, {}, { headers })
        .pipe(
          catchError(error => {
            console.error('[Auth Service] Error during logout API call:', error);
            return of(null);
          })
        )
        .subscribe({
          next: () => {
            console.log('[Auth Service] Logout API call successful');
          },
          complete: () => {
            // Clear local auth data regardless of API response
            this.clearAuthData();
            console.log('[Auth Service] User logged out and local auth data cleared');
          }
        });
    } else {
      // If no token, just clear local auth data
      this.clearAuthData();
      console.log('[Auth Service] No token present, cleared local auth data only');
    }
  }
  
  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('auth_token');
  }
  
  /**
   * Get current token
   */
  getToken(): string | null {
    const token = localStorage.getItem('auth_token');
    return token ? token.trim() : null;
  }
  
  /**
   * Refresh authentication state
   */
  refreshAuthState(): void {
    const token = localStorage.getItem('auth_token');
    
    if (token) {
      const cleanToken = token.trim();
      // Update token if needed
      if (cleanToken !== token) {
        localStorage.setItem('auth_token', cleanToken);
      }
      
      this.isAuthenticatedSubject.next(true);
      
      // Recreate user info from localStorage
      const userInfo: UserInfo = {
        id: parseInt(localStorage.getItem('userId') || '0', 10),
        username: localStorage.getItem('username') || 'Unknown',
        email: localStorage.getItem('email') || '',
        role: localStorage.getItem('userRole') || 'Unknown',
        company_id: localStorage.getItem('companyId') ? 
          parseInt(localStorage.getItem('companyId') || '0', 10) : undefined,
        token: cleanToken
      };
      
      this.userInfoSubject.next(userInfo);
      console.log('[Auth Service] Auth state refreshed, user is authenticated');
    } else {
      this.isAuthenticatedSubject.next(false);
      this.userInfoSubject.next(null);
      console.log('[Auth Service] Auth state refreshed, user is not authenticated');
    }
  }
  
  /**
   * Clear all authentication data
   */
  private clearAuthData(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('username');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    localStorage.removeItem('companyId');
    localStorage.removeItem('email');
    
    this.isAuthenticatedSubject.next(false);
    this.userInfoSubject.next(null);
    
    console.log('[Auth Service] Authentication data cleared');
  }
  
  /**
   * Check if current user is a superuser
   * @returns Observable<boolean> True if user is a superuser
   */
  checkSuperuser(): Observable<boolean> {
    console.log('[Auth Service] Checking if user is a superuser');
    
    // If not authenticated, return false immediately
    if (!this.isAuthenticated()) {
      console.log('[Auth Service] User not authenticated, cannot be superuser');
      return of(false);
    }
    
    const headers = this.getAuthHeaders();
    
    return this.http.get<SuperuserCheckResponse>(`${this.apiUrl}/auth/check-superuser`, { headers })
      .pipe(
        tap(response => {
          console.log('[Auth Service] Superuser check response:', response);
        }),
        map(response => response.isSuperuser),
        catchError(error => {
          return of(false);
        })
      );
  }
  
  /**
   * Check if current user is a superuser based on stored role
   * This is a synchronous check based on local data, not a server call
   * @returns boolean True if user has superuser role
   */
  isSuperuser(): boolean {
    const userInfo = this.userInfoSubject.getValue();
    return !!(userInfo && userInfo.role === 'superuser');
  }
} 